import type { ReactElement, ReactNode } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { STACKED_BAR_CHART_BAR_COLORS } from './constants'
import { StackedBarChartBar } from './stacked-bar-chart-bar'
import { StackedBarChartContext } from './stacked-bar-chart-context'
import type { StackedBarHoveredState } from './types'

interface StackedBarChartProps {
  width: number
  height: number
  colors?: string[]
  children: ReactNode
}

export const StackedBarChart: React.FC<StackedBarChartProps> = ({ width, height, colors, children }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [hovered, setHovered] = useState<StackedBarHoveredState | null>(null)
  const [tooltipHovered, setTooltipHovered] = useState<StackedBarHoveredState | null>(null)

  // Separate out StackedBarChartBar child so it renders first. Rest of the
  // children are stacked above the bar.
  const allChildren = React.Children.toArray(children) as ReactElement[]
  const barChild = allChildren.find((x) => x.type === StackedBarChartBar)
  const otherChildren = allChildren.filter((x) => x !== barChild)

  const handleMouseLeave = useCallback(() => {
    setHovered((prev) => {
      if (!prev) return null
      if (prev.locked) return prev
      return null
    })
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setHovered(null)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  return (
    <StackedBarChartContext.Provider
      value={{
        width,
        height,
        hovered,
        setHovered,
        tooltipHovered,
        setTooltipHovered,
        colors: colors ?? STACKED_BAR_CHART_BAR_COLORS,
      }}
    >
      <div ref={containerRef} style={{ position: 'relative', width, height }} onMouseLeave={handleMouseLeave}>
        {/* Bar */}
        {barChild && React.cloneElement(barChild, { key: 'bar' })}

        {/* Other children: tooltip, etc. */}
        {otherChildren.map((child, i) => {
          return React.cloneElement(child, { key: child.key ?? i, container: containerRef })
        })}
      </div>
    </StackedBarChartContext.Provider>
  )
}
